import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Spinner, Text } from '@chakra-ui/react';
import auth from '@udacity/ureact-hoth';

import { getAgreementHeading } from 'app/agreements/helpers/helpers';
import { AGREEMENT_STATUS_PENDING_SIGNATURE, AGREEMENT_STATUS_SIGNED, getLabelForStatus } from 'app/agreements/status';
import { Agreement, CountryLocale } from 'app/agreements/types';
import { AgreementModal } from 'app/agreements/components/agreement-modal';
import { getCountryLocaleDataForRole } from 'app/agreements/locale-data';
import { DataTable } from 'app/common/components/data-table';
import { useFetchMeQuery } from 'app/apis/reviews';
import { useAppToast } from 'app/common/hooks/use-app-toast';
import { agreementDate } from 'app/helpers/format-helper';
import { Status } from 'app/types/mentor-gql';
import {
  useGetMentorAgreementsQuery,
  useGetMentorQuery,
  useUpdateMentorAgreementMutation,
} from 'app/mentor/queries/enhanced';

import styles from './styles.module.scss';

export const AgreementsList: React.FC = () => {
  const [selectedAgreement, setSelectedAgreement] = useState<null | Agreement>(null);
  const [countryLocale, setCountryLocale] = useState<undefined | CountryLocale>();

  const uid = auth.getCurrentUserId() || '';
  const { data: mentorData } = useGetMentorQuery({ uid }, { skip: !uid });
  const { data: reviewsMe } = useFetchMeQuery();
  const mentorName = reviewsMe?.name ?? '';
  const mentorCountryCode = mentorData?.mentor?.country ?? '';

  const { t } = useTranslation();
  const toast = useAppToast();
  const { data, isLoading } = useGetMentorAgreementsQuery({ mentor_id: uid }, { skip: !uid });
  const [updateMentorAgreement] = useUpdateMentorAgreementMutation();
  const agreements = (data?.agreements ?? []).filter(Boolean) as unknown as Agreement[];

  function selectAgreement(agreement: Agreement) {
    const localeData = getCountryLocaleDataForRole(
      agreement.role,
      mentorCountryCode,
      agreement.currency_override || ''
    );
    setCountryLocale(localeData);
    setSelectedAgreement(agreement);
  }

  function onAccept(agreement: Agreement, countryLocale: CountryLocale) {
    const payRate = parseInt(countryLocale.payRatePerHour.replace('$', '').replace('£', '')).toString();

    const maxPaymentPerMonth = parseInt(countryLocale.maxPaymentPerMonth.replace('$', '').replace('£', '')).toString();

    const bonus = countryLocale.bonus
      ? parseInt(countryLocale.bonus.replace('$', '').replace('£', '')).toString()
      : '0';

    const data: Agreement = {
      ...agreement,
      status: AGREEMENT_STATUS_SIGNED,
      date_signed: new Date(),
      pay_rate: payRate,
      hours_per_month: countryLocale.upToHoursPerMonth,
      max_payments_per_month: maxPaymentPerMonth,
      bonus: bonus,
    };

    updateMentorAgreement({
      id: data.id,
      mentor_id: data.mentor_id,
      status: Status.Signed,
      date_signed: data.date_signed,
      pay_rate: data.pay_rate,
      hours_per_month: data.hours_per_month,
      max_payments_per_month: data.max_payments_per_month,
      bonus: data.bonus,
    })
      .unwrap()
      .then(() => {
        setSelectedAgreement(null);
      })
      .catch((error) => {
        console.error({ error });
        toast({
          variant: 'error',
          description: t('error.somethingWentWrong'),
        });
      });
  }

  const tableData = agreements.map((row) => {
    const title = getAgreementHeading(row.role);
    const buttonLabel = row.status === AGREEMENT_STATUS_SIGNED ? t('common.view') : t('common.review');
    const buttonVariant = row.status === AGREEMENT_STATUS_PENDING_SIGNATURE ? 'solid' : 'outline';
    const status = getLabelForStatus(row.status);
    const date = row.date_signed ? agreementDate(row.date_signed) : '';

    return {
      [t('table.columns.name')]: {
        data: title,
      },
      [t('table.columns.status')]: {
        data: (
          <div>
            {status}
            <br />
            <small>{date}</small>
          </div>
        ),
      },
      [t('table.columns.action')]: {
        data: (
          <Button onClick={() => selectAgreement(row)} variant={buttonVariant} size="sm">
            {buttonLabel}
          </Button>
        ),
      },
    };
  });

  return (
    <div>
      <header className={styles.header}>
        <Heading size="lg" as="h2">
          {t('agreements.yourAgreements')}
        </Heading>
      </header>

      <main className={styles.agreementTableContainer}>
        {isLoading ? (
          <Spinner />
        ) : agreements.length <= 0 ? (
          <Text>{t('agreements.youDontHaveAny')}</Text>
        ) : (
          <DataTable rows={tableData} />
        )}
      </main>

      {selectedAgreement && (
        <AgreementModal
          agreement={selectedAgreement}
          mentorCountryCode={mentorCountryCode}
          countryLocale={countryLocale}
          mentorName={mentorName}
          onAccept={onAccept}
          onClose={() => setSelectedAgreement(null)}
        />
      )}
    </div>
  );
};
